/**
 * 患者のログインの画面（/patient/login。Issue #473）。ops/backoffice の authentication/Authentication.tsx を手本にする。
 *
 * 「ログイン ID とパスワード」→「認証コード」→「新しいパスワード（要るときだけ）」→ トークンの発行、の段階の状態を持つ。
 * この画面は web のフラグが ON のときだけ開ける（router.tsx）ので、移行の案内は常に出す（M10 でフラグと一緒に消す）。
 */
import { useEffect, useState } from "react";

import {
  type OneTimePasswordSender,
  issueTokens,
} from "./patientAuthenticationApi";
import { errorMessageOf } from "./patientAuthErrors";
import { PageLayout, SupportContact } from "./patientAuthenticationParts";
import { PatientInitialPasswordForm } from "./PatientInitialPasswordForm";
import { PATIENT_HOME_PATH, type PatientLoginNotice } from "./patientLoginPath";
import { PatientOneTimePasswordForm } from "./PatientOneTimePasswordForm";
import { PatientSignInForm } from "./PatientSignInForm";
import {
  currentPatientSession,
  subscribePatientSessionChange,
} from "./patientSession";

type Phase = { name: "signIn"; error: string } | {
  name: "oneTimePassword";
  oneTimeToken: string;
  sender: OneTimePasswordSender;
} | { name: "initialPassword"; oneTimeToken: string };

/** URL の notice の値。決まった 2 つの値だけを受け付け、それ以外は無視する。 */
function noticeOf(search: string): PatientLoginNotice | null {
  const value = new URLSearchParams(search).get("notice");
  return value === "expired" || value === "signed-out" ? value : null;
}

/** 案内を出したら URL から notice を落とす（ブックマークや再読み込みで同じ案内が出続けないため）。 */
function dropNoticeFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("notice")) return;
  url.searchParams.delete("notice");
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

/** ログインの後は常に患者のホームへ（戻り先の URL は持たない）。前の利用者の画面の状態を残さないよう、読み込み直す。 */
const goToPatientHome = () => window.location.replace(PATIENT_HOME_PATH);

type Props = {
  onSignedIn?: () => void;
};

export function PatientAuthentication({ onSignedIn = goToPatientHome }: Props) {
  const [notice, setNotice] = useState(() => noticeOf(window.location.search));
  const [phase, setPhase] = useState<Phase>({ name: "signIn", error: "" });
  const [loginId, setLoginId] = useState("");
  const [signInKey, setSignInKey] = useState(0);

  useEffect(() => {
    dropNoticeFromUrl();
  }, []);

  // ほかのタブでログインしたら、この画面からもホームへ移す（ここで別の患者がログインして、ほかのタブの患者の
  // セッションを失効させないまま上書きしないため）。ログイン済みで開いたときは router.tsx がホームへ移す
  useEffect(
    () =>
      subscribePatientSessionChange(({ inOtherTab }) => {
        if (inOtherTab && currentPatientSession() !== null) onSignedIn();
      }),
    [onSignedIn],
  );

  // 戻るたびにフォームを作り直し、エラーと入力を初めの状態にする（ログイン ID は残す）
  const backToSignIn = (message: string) => {
    setSignInKey((key) => key + 1);
    setPhase({ name: "signIn", error: message });
  };

  const finish = async (oneTimeToken: string) => {
    try {
      await issueTokens(oneTimeToken);
      onSignedIn();
    } catch (e) {
      // 発行に失敗したワンタイムトークンは使い直せないので、どの失敗でも最初の段へ戻す
      backToSignIn(errorMessageOf(e));
    }
  };

  return (
    <PageLayout>
      {phase.name === "signIn" && (
        <PatientSignInForm
          key={signInKey}
          initialLoginId={loginId}
          initialError={phase.error}
          notice={notice}
          focusHeadingOnMount={signInKey > 0}
          onOneTimePasswordRequired={(id, oneTimeToken, sender) => {
            setLoginId(id);
            setNotice(null);
            setPhase({ name: "oneTimePassword", oneTimeToken, sender });
          }}
          onSignedIn={onSignedIn}
        />
      )}
      {phase.name === "oneTimePassword" && (
        <PatientOneTimePasswordForm
          oneTimeToken={phase.oneTimeToken}
          sender={phase.sender}
          onAuthenticated={(oneTimeToken, requirePasswordChange) => {
            if (requirePasswordChange) {
              setPhase({ name: "initialPassword", oneTimeToken });
              return;
            }
            return finish(oneTimeToken);
          }}
          onSessionExpired={backToSignIn}
          onRestart={() => backToSignIn("")}
        />
      )}
      {phase.name === "initialPassword" && (
        <PatientInitialPasswordForm
          oneTimeToken={phase.oneTimeToken}
          onSignedIn={onSignedIn}
          onSessionExpired={backToSignIn}
        />
      )}
      <SupportContact />
    </PageLayout>
  );
}
